import { Formio } from '@formio/js';
import { fileDisplayUrl } from '../helpers/file-url.helper';

/**
 * ---------------------------------------------------------------------------
 * Le champ « fichier », au Design System GNDAD
 * ---------------------------------------------------------------------------
 *
 * PORTAGE. Le markup et le style viennent de la librairie GNDAD
 * (`@gndad/components` — `src/components/File.js`, et `@gndad/theme` —
 * `src/scss/_upload.scss`). Ils sont repris ici plutôt qu'installés en
 * dépendance : la librairie n'est pas publiée sur un registre, elle vit dans un
 * dépôt à part, et son composant tire huit symboles de `@gndad/core` — on
 * embarquerait le socle entier pour un seul champ.
 *
 * CE QUI EST REPRIS, ET POURQUOI CE FICHIER EXISTE
 * ------------------------------------------------
 * Ce que Form.io rend pour un champ `file` est un tableau à quatre colonnes
 * (nom | taille | type MIME | corbeille) dans un `ul.list-group`. Cette
 * structure ne laisse aucune place à une vignette, et c'est elle qui casse dès
 * qu'un nom de fichier est long — le correctif de mise en page qui la retenait
 * occupe la première moitié de `src/styles/_cf-upload.scss`.
 *
 * Le composant dessine donc son propre markup : une zone de dépôt, une tuile
 * par fichier avec sa vignette, une ligne de progression par envoi en cours.
 * Toutes les classes `gndad-file-*` lui appartiennent, et c'est ce qui rend le
 * style opérant : appliquer la feuille GNDAD au tableau natif n'aurait ciblé
 * aucun élément de la page.
 *
 * COMMENT IL SE BRANCHE
 * ---------------------
 * Par `renderTemplate`, jamais par `render()`. `File.render()` ne prend aucun
 * argument : il ignore ce qu'on lui passe et appelle `renderTemplate('file')`
 * lui-même, si bien que `super.render(monMarkup)` rendrait le libellé, la
 * description, et rien entre les deux. Répondre au nom du gabarit laisse en
 * place le `render`, l'`attach` et toute la tuyauterie d'upload de Form.io.
 *
 * CE QUI EST HÉRITÉ TEL QUEL — et qu'il ne faut donc pas réécrire :
 * `browseFiles`, `handleFilesToUpload`, `getFile`, `filesToSync`, la validation
 * de `filePattern` / `fileMinSize` / `fileMaxSize`, les storages base64 et url.
 *
 * DEUX ATTACHES DU PORTAIL QUI DOIVENT SURVIVRE
 * ---------------------------------------------
 * 1. `formio-library-upload.bridge.ts` intercepte en capture les clics sur
 *    `[ref="fileBrowse"]` et retrouve le composant par
 *    `component.refs.fileBrowse`. Le markup ci-dessous garde donc ce `ref` sur
 *    le bouton « Parcourir les fichiers » : le choix bibliothèque / appareil
 *    continue de fonctionner sans une ligne de changement.
 * 2. `formio-schema-fixes.utils.ts` préfixe et déprefixe les URLs de fichiers
 *    legacy dans la soumission. Ce composant ne touche jamais à `file.url` : il
 *    le lit pour l'affichage, et rien de plus.
 *
 * CE QUI N'EST PAS REPRIS, délibérément :
 * - la compression d'image à l'envoi (`compressorjs`) — une dépendance de plus
 *   pour un comportement que personne n'a demandé ici ;
 * - la lecture des storages signés (`readSignedFile`) — le portail a son propre
 *   `formio-legacy-url-provider` ;
 * - l'aperçu dans la fenêtre modale du socle GNDAD — remplacé par une modale
 *   locale, qui rend les mêmes classes `gndad-file-preview*`.
 */

/** Le composant natif dont on hérite, résolu à l'enregistrement. */
type FormioComponentClass = new (...args: any[]) => any;

/**
 * Les libellés passent tous par `this.t()`, donc par le dictionnaire i18n monté
 * dans `FormIoService.getFormioOptions()`. Les quatre premières clés y étaient
 * déjà — elles servaient au gabarit natif et sont reprises telles quelles, ce
 * qui évite de traduire deux fois la même phrase.
 */
const T = {
  DROP: 'Drop files to attach,',
  BROWSE: 'Browse Files',
  HEAD_NAME: 'File Name',
  HEAD_SIZE: 'Size',
  PREVIEW: 'Preview',
  PREVIEW_UNAVAILABLE: 'Preview unavailable for this format',
  DOWNLOAD: 'Download',
  REMOVE: 'Remove',
  CLOSE: 'Close',
  ACCEPTED_FORMATS: 'Accepted formats',
  MAX_SIZE: 'Maximum size',
  NONE: 'No file',
  UNIT_B: 'bytes',
  UNIT_KB: 'KB',
  UNIT_MB: 'MB',
  UNIT_GB: 'GB',
} as const;

/** Extensions traitées comme des images pour la vignette et l'aperçu. */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif'];

/** Au-delà, le nom est coupé — au milieu, pour garder l'extension lisible. */
const NAME_MAX_LENGTH = 34;

/**
 * Construit la classe du composant à partir de la classe native.
 *
 * En fabrique plutôt qu'en `class … extends` au niveau du module : la classe
 * native n'existe qu'une fois `@formio/js` chargé, et un `extends` évalué à
 * l'import échouerait si l'ordre des modules venait à changer.
 */
function createGndadFileComponent(NativeFile: FormioComponentClass): FormioComponentClass {
  return class GndadFileComponent extends NativeFile {

    /** URLs d'objet créées pour les aperçus locaux, à révoquer au retrait. */
    private _localUrls = new Map<string, string>();

    // -----------------------------------------------------------------------
    // Le branchement au rendu de Form.io
    // -----------------------------------------------------------------------

    /**
     * Le gabarit `file` est intercepté ; tous les autres passent au natif.
     *
     * Pas en mode atelier : on y compose un formulaire, on n'y dépose pas de
     * fichier, et l'aperçu du builder attend la structure que Form.io connaît.
     */
    renderTemplate(name: string, ...rest: any[]): string {
      if (name === 'file' && !(this as any).builderMode) {
        return this.renderControl();
      }
      return super.renderTemplate(name, ...rest);
    }

    /**
     * Le champ ne prend plus de saisie.
     *
     * Deux sources, parce que ce sont deux questions à la même réponse :
     * `disabled` est l'état du composant — posé par une condition, par un
     * panneau parent, ou par `FormIoService.disableFields()` ; `shouldDisabled`
     * est celui du formulaire — `options.readOnly`, ou `disabled` dans la
     * définition. Le portail rend des dossiers en lecture seule par les deux
     * chemins.
     */
    get locked(): boolean {
      return Boolean((this as any).disabled || (this as any).shouldDisabled);
    }

    // -----------------------------------------------------------------------
    // Le markup
    // -----------------------------------------------------------------------

    /** Le contenu du champ, en remplacement du tableau natif. */
    renderControl(): string {
      const self = this as any;
      const files: any[] = Array.isArray(self.dataValue) ? self.dataValue : [];
      // TOUS les états de la file d'envoi, pas seulement `progress`.
      //
      // N'en rendre que les envois en cours revenait à **taire les échecs** :
      // Form.io pousse un fichier refusé — mauvais format, trop lourd, envoi en
      // erreur — dans cette même liste avec `status: 'error'` et son message.
      // Filtré, il ne s'affichait nulle part : l'usager cliquait, déposait, et
      // rien ne se passait. Pas d'erreur, pas de tuile, aucune réaction. Un
      // échec muet est pire qu'un échec, parce qu'il se lit comme une panne du
      // formulaire plutôt que comme un fichier à remplacer.
      const enAttente: any[] = self.filesToSync?.filesToUpload || [];
      const complet = !self.component.multiple && files.length > 0;
      const locked = this.locked;

      // Un champ verrouillé ne dessine pas de zone grisée : un rectangle en
      // pointillés qui dit « déposez ici » est une invitation, et une invitation
      // sans effet se lit comme une panne. Sans fichier il resterait un libellé
      // au-dessus d'un vide, alors il le dit.
      const zone = locked
        ? (files.length ? '' : `<p class="gndad-file-empty">${self.sanitize(self.t(T.NONE))}</p>`)
        : `
        <div class="gndad-file-drop" ref="fileDropZone" tabindex="0" role="button">
          <span class="gndad-icon gndad-icon-upload" aria-hidden="true"></span>
          <span class="gndad-file-drop-text">${self.sanitize(self.t(T.DROP))}</span>
          <button type="button" class="gndad-file-browse" ref="fileBrowse">
            <span class="gndad-icon gndad-icon-folder" aria-hidden="true"></span>
            <span class="gndad-file-browse-text">${self.sanitize(self.t(T.BROWSE))}</span>
          </button>
        </div>`;

      // L'en-tête « Nom du fichier | Taille » est là dès l'état initial, avant
      // tout dépôt : il annonce ce que le champ attend et ce qu'il montrera. Un
      // cartouche qui apparaîtrait au premier fichier ferait sauter la mise en
      // page sous les yeux de l'usager, au moment précis où il attend un retour
      // sur son dépôt. Pas au-dessus d'une zone verrouillée en revanche : là, il
      // n'y aura jamais de colonnes.
      const entete = locked && !files.length
        ? ''
        : `
        <div class="gndad-file-head" aria-hidden="true">
          <span class="gndad-file-head-name">${self.sanitize(self.t(T.HEAD_NAME))}</span>
          <span class="gndad-file-head-size">${self.sanitize(self.t(T.HEAD_SIZE))}</span>
        </div>`;

      // Les fichiers reçus d'abord, la zone de dépôt en dessous : ce qui vient
      // d'être déposé se lit là où le regard est déjà, et l'invitation à en
      // ajouter un autre vient après. L'ordre inverse repousse chaque nouveau
      // fichier plus bas et oblige à chercher ce qu'on vient d'ajouter.
      const liste = files.length
        ? `<ul class="gndad-file-list" ref="fileList">${files.map((f, i) => this.renderTile(f, i)).join('')}</ul>`
        : '';
      const progression = enAttente.length
        ? `<ul class="gndad-file-progress-list">${enAttente.map((s, i) => this.renderProgress(s, i)).join('')}</ul>`
        : '';

      return `
      <div class="gndad-file${locked ? ' is-locked' : ''}">
        ${entete}
        ${liste}
        ${progression}
        ${complet ? '' : zone}
      </div>`;
    }

    /** Une tuile : vignette, nom, poids, actions. */
    renderTile(file: any, index: number): string {
      const self = this as any;
      const url = this.thumbnailUrl(file);
      const style = this.thumbnailStyle;
      // `loading="lazy"` : un dossier rouvert peut porter dix pièces, et leurs
      // vignettes sont alors les seuls fichiers que le navigateur va rechercher
      // sur le serveur — celles qui viennent d'être déposées sont déjà en main.
      // Repousser celles qu'on ne voit pas est ce qui évite dix téléchargements
      // pour deux tuiles à l'écran.
      const vignette = url
        ? `<img class="gndad-file-thumb" ref="fileThumb" alt="" loading="lazy" decoding="async" style="${style}">`
        : `<span class="gndad-file-thumb gndad-file-thumb-generic" style="${style}" aria-hidden="true"></span>`;

      const actions: string[] = [];
      if (self.component.previewButton !== false) {
        // Grisé sur un PDF, et sur tout ce qui n'est pas une image.
        //
        // ATTENTION — la raison historiquement écrite ici était fausse sur les
        // deux points qu'elle avançait, et la mesure sur la sandbox le montre :
        // `/file/public/{uid}` EST confirmée en lecture, elle rend du JSON de
        // métadonnées et jamais le fichier ; et `/file/{uid}/download/public`
        // ne répond PAS en pièce jointe, mais `Content-Disposition: inline`,
        // quel que soit `isAttachment`. Rien n'empêche donc techniquement
        // d'afficher un PDF dans le cadre.
        //
        // Le grisage est conservé tel quel en attendant l'arbitrage, pour ne
        // pas changer le comportement visible dans le même passage que la
        // correction d'adresse. Le lever tient en une ligne : accepter aussi
        // `application/pdf` dans `affichable`.
        //
        // Désactivé plutôt que retiré : les tuiles gardent le même nombre de
        // commandes, donc la même largeur, et la liste ne se déforme pas selon
        // les formats déposés. L'infobulle dit pourquoi il ne répond pas — un
        // bouton gris sans explication se lit comme une panne.
        const affichable = this.isImage(file);
        actions.push(this.actionButton(
          'preview', 'eye',
          affichable ? self.t(T.PREVIEW) : self.t(T.PREVIEW_UNAVAILABLE),
          index,
          !affichable,
        ));
      }
      if (self.component.downloadButton !== false) {
        actions.push(this.actionButton('download', 'download', self.t(T.DOWNLOAD), index));
      }
      // L'aperçu et le téléchargement survivent au verrouillage — lire un
      // document qu'on n'a pas le droit de modifier est exactement ce qu'on
      // vient faire sur un dossier en lecture seule. C'est le retrait qui doit
      // disparaître.
      if (self.component.removeButton !== false && !this.locked) {
        actions.push(this.actionButton('remove', 'trash', self.t(T.REMOVE), index));
      }

      const nom = this.displayName(file);
      return `
      <li class="gndad-file-tile" ref="fileTile">
        ${vignette}
        <div class="gndad-file-body">
          <span class="gndad-file-name" title="${self.sanitize(nom)}">${self.sanitize(this.shortName(nom))}</span>
          <span class="gndad-file-size">${self.sanitize(this.fileSize(this.sizeBytes(file)))}</span>
        </div>
        <div class="gndad-file-actions">${actions.join('')}</div>
      </li>`;
    }

    /**
     * Un bouton d'action de tuile.
     *
     * L'icône est un masque CSS rempli en `currentColor`, pas un glyphe de
     * police : les trois commandes portaient des classes Bootstrap Icons, dont
     * le codepoint privé n'existe que dans une fonte que la page doit avoir
     * chargée. Fonte absente, tardive, évincée du cache ou refusée par une CSP,
     * le bouton sort vide — trois carrés à côté d'un fichier, et rien pour dire
     * lequel le supprime.
     */
    private actionButton(
      action: string, glyphe: string, libelle: string, index: number, inactif = false,
    ): string {
      const self = this as any;
      const texte = self.sanitize(libelle);
      // `disabled` et non une simple classe : c'est lui qui retire le bouton du
      // parcours de tabulation et le fait annoncer comme indisponible par un
      // lecteur d'écran. Une commande seulement grise reste atteignable au
      // clavier et ne dit rien de son état.
      return `<button type="button" class="gndad-file-action" data-file-action="${action}"`
        + ` data-file-index="${index}" aria-label="${texte}" title="${texte}"`
        + `${inactif ? ' disabled aria-disabled="true"' : ''}>`
        + `<i class="gndad-icon gndad-icon-${glyphe}" aria-hidden="true"></i></button>`;
    }

    /**
     * Une ligne de la file d'envoi : en cours, ou en échec.
     *
     * Le bouton de retrait est posé sur **chaque** ligne, quel que soit son
     * état, et ce n'est pas une commodité : Form.io branche `fileToSyncRemove`
     * par position — `refs.fileToSyncRemove[i]` retire `filesToUpload[i]`. Ne le
     * poser que sur les lignes en erreur décalerait les index dès qu'un envoi en
     * cours les précède, et le clic retirerait la mauvaise ligne.
     *
     * @param status - Une description d'envoi de Form.io.
     * @param index - Sa position dans `filesToUpload`.
     */
    renderProgress(status: any, index = 0): string {
      const self = this as any;
      const enErreur = status.status === 'error';
      const nom = self.sanitize(this.shortName(this.displayName(status)));
      const message = self.sanitize(status.message || '');

      // Sans `ref`, Form.io ne branche rien ; avec, le retrait est gratuit.
      const retrait = `<button type="button" class="gndad-file-action" ref="fileToSyncRemove"`
        + ` data-file-index="${index}" aria-label="${self.sanitize(self.t(T.REMOVE))}"`
        + ` title="${self.sanitize(self.t(T.REMOVE))}">`
        + `<i class="gndad-icon gndad-icon-trash" aria-hidden="true"></i></button>`;

      if (enErreur) {
        return `
      <li class="gndad-file-progress-row is-error">
        <div class="gndad-file-progress-head">
          <span class="gndad-file-name">${nom}</span>
          ${retrait}
        </div>
        <p class="gndad-file-error" role="alert">${message}</p>
      </li>`;
      }

      const pourcent = Number(status.progress) || 0;
      return `
      <li class="gndad-file-progress-row">
        <div class="gndad-file-progress-head">
          <span class="gndad-file-name">${nom}</span>
          ${retrait}
        </div>
        <div class="gndad-file-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100"
          aria-valuenow="${pourcent}">
          <div class="gndad-file-progress-bar" ref="progress" id="${self.sanitize(status.id || '')}"
            style="width:${pourcent}%"></div>
        </div>
        <span class="gndad-file-progress-text">${message}</span>
      </li>`;
    }

    // -----------------------------------------------------------------------
    // Le montage
    // -----------------------------------------------------------------------

    attach(element: HTMLElement): any {
      const self = this as any;
      element.classList.add('gndad-file-component');
      const resultat = super.attach(element);

      self.loadRefs(element, {
        fileTile: 'multiple',
        fileList: 'single',
        fileThumb: 'multiple',
        fileDropZone: 'single',
      });

      this.paintThumbnails();
      this.attachConstraints();

      // Une seule écoute sur la liste plutôt qu'une par bouton : les tuiles vont
      // et viennent à chaque envoi, et une écoute posée sur une tuile lui
      // survit.
      if (self.refs.fileList) {
        self.addEventListener(self.refs.fileList, 'click', (event: Event) => this.onTileAction(event));
      }

      // Le dépôt est géré ici, et la zone porte `ref="fileDropZone"` et non
      // `fileDrop` : le gestionnaire natif écrase `className` à chaque survol
      // (`this.className = 'fileSelector fileDragOver'`), ce qui emporterait la
      // classe qui habille la zone. Un nom de `ref` différent laisse le natif
      // sans prise, et l'état de survol passe par une classe qui s'ajoute au
      // lieu de tout remplacer.
      const zone: HTMLElement | undefined = self.refs.fileDropZone;
      if (zone && !this.locked) {
        self.addEventListener(zone, 'dragover', (event: DragEvent) => {
          event.preventDefault();
          zone.classList.add('is-dragover');
        });
        self.addEventListener(zone, 'dragleave', (event: DragEvent) => {
          event.preventDefault();
          zone.classList.remove('is-dragover');
        });
        self.addEventListener(zone, 'drop', (event: DragEvent) => {
          event.preventDefault();
          zone.classList.remove('is-dragover');
          const fichiers = event.dataTransfer?.files;
          if (fichiers && fichiers.length) {
            self.handleFilesToUpload(fichiers);
          }
        });
        // Toute la boîte ouvre le sélecteur, pas seulement le mot qu'elle
        // contient : une cible de la taille d'une phrase est une cible qu'on
        // manque au pouce. Le clic passe par le bouton plutôt que par
        // `browseFiles` directement, pour que le pont bibliothèque — qui écoute
        // `[ref="fileBrowse"]` en capture — reste sur le chemin.
        self.addEventListener(zone, 'click', (event: MouseEvent) => {
          if ((event.target as Element)?.closest?.('.gndad-file-browse')) return;
          event.preventDefault();
          self.refs.fileBrowse?.click();
        });
        self.addEventListener(zone, 'keydown', (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            self.refs.fileBrowse?.click();
          }
        });
      }

      return resultat;
    }

    /**
     * Poser la source de chaque vignette après le montage.
     *
     * Parcouru sur les fichiers qui EN ONT une, parce qu'une tuile sans image
     * rend une pastille et non un `<img>` : les `refs` et les fichiers ne sont
     * donc pas la même liste, et les apparier par position mettrait la photo
     * d'un fichier sur la tuile d'un autre dès qu'un PDF précède une image.
     *
     * La source est posée sur l'élément plutôt qu'écrite dans le markup :
     * Form.io passe ce qu'un composant rend par DOMPurify, dont la politique
     * d'URI par défaut refuse `blob:` — l'attribut serait retiré en silence dans
     * le cas précis où il sert, l'aperçu local d'un fichier tout juste choisi.
     */
    paintThumbnails(): void {
      const self = this as any;
      const images: HTMLImageElement[] = self.refs.fileThumb || [];
      const avecVignette = (self.dataValue || []).filter((file: any) => this.thumbnailUrl(file));
      images.forEach((image, index) => {
        const url = this.thumbnailUrl(avecVignette[index]);
        if (url) image.src = url;
      });
    }

    /**
     * Poser l'annonce des contraintes sous la zone de dépôt.
     *
     * Sous, et non au-dessus : au-dessus, elle sépare le libellé de la commande
     * qu'il nomme. C'est un texte d'aide, elle en prend la place.
     *
     * Construite au montage plutôt qu'écrite dans le markup : Form.io ne
     * l'affiche qu'après coup, dans un message d'erreur, et seulement à celui
     * qui s'est trompé — l'usager qui hésite entre deux fichiers n'a rien à
     * lire.
     */
    attachConstraints(): void {
      const self = this as any;
      const zone: HTMLElement | null = self.element?.querySelector('.gndad-file-drop') ?? null;
      const existante: HTMLElement | null = self.element?.querySelector('.gndad-upload-hint') ?? null;
      if (!zone) {
        existante?.remove();
        return;
      }
      const texte = this.constraintsText();
      if (!texte) {
        existante?.remove();
        return;
      }
      if (existante) {
        existante.textContent = texte;
        return;
      }
      const hint = document.createElement('p');
      hint.className = 'gndad-upload-hint';
      hint.textContent = texte;
      zone.insertAdjacentElement('afterend', hint);
    }

    // -----------------------------------------------------------------------
    // Les actions d'une tuile
    // -----------------------------------------------------------------------

    onTileAction(event: Event): void {
      const self = this as any;
      const declencheur = (event.target as Element)?.closest?.('[data-file-action]') as HTMLElement | null;
      if (!declencheur) return;
      event.preventDefault();

      // Le retrait, et lui seul. Un redessin peut retarder d'une image un
      // `disable()`, et un bouton de suppression périmé, c'est un document
      // retiré d'un dossier dont on avait dit à son propriétaire qu'il ne
      // pouvait plus y toucher.
      const action = declencheur.dataset['fileAction'];
      if (this.locked && action === 'remove') return;

      const index = Number(declencheur.dataset['fileIndex']);
      const file = (self.dataValue || [])[index];
      if (!file) return;

      switch (action) {
        // Une seconde garde, en plus de l'attribut `disabled` : `previewFile`
        // reste appelable depuis le JS custom d'un formulaire, et sur un
        // document elle ouvrirait un cadre que le service ne sait pas remplir.
        case 'preview': if (this.isImage(file)) this.previewFile(file); break;
        case 'download': this.downloadFile(file); break;
        case 'remove': this.removeFile(index); break;
        default: break;
      }
    }

    /**
     * Garder sous la main l'image que l'usager vient de choisir.
     *
     * Sans cela, la tuile qui s'affiche à la seconde où le fichier est déposé
     * pointe sur l'adresse que le serveur vient de rendre — et le navigateur
     * **retélécharge aussitôt le fichier qui vient de partir**, en taille
     * réelle, pour le peindre dans un carré de 64 px. Sur une capture d'écran de
     * quelques mégaoctets et une connexion étroite, l'aller-retour est
     * intégralement gratuit : les octets étaient dans la page une seconde plus
     * tôt.
     *
     * La clé se lit des deux côtés de l'envoi : un `File` du navigateur porte
     * `name`, le descripteur qui revient du serveur porte `originalName` et un
     * `name` réécrit avec un identifiant. `displayName()` lit l'un ou l'autre,
     * et le poids ne change pas en route.
     */
    rememberLocal(file: any): void {
      if (!this.isImage(file) || typeof URL?.createObjectURL !== 'function') return;
      const cle = this.localKey(file);
      if (this._localUrls.has(cle)) return;
      this._localUrls.set(cle, URL.createObjectURL(file));
    }

    /**
     * La couture où l'image choisie est retenue, avant que Form.io l'envoie.
     *
     * Ici plutôt que dans `attach` : c'est le seul point par lequel passent les
     * trois chemins d'arrivée d'un fichier — le sélecteur du système, le
     * glisser-déposer, et la bibliothèque de documents du portail, qui appelle
     * cette méthode elle aussi (`formio-library-upload.bridge.ts`).
     */
    handleFilesToUpload(files: FileList | File[]): any {
      Array.from(files || []).forEach((file) => this.rememberLocal(file));
      return super.handleFilesToUpload(files);
    }

    /**
     * Retire un fichier — de la valeur ET du stockage.
     *
     * Délégué à `handleFileToRemove()`, le chemin de Form.io, plutôt que réécrit.
     * Une première version se contentait d'ôter l'entrée de `dataValue` : la
     * tuile disparaissait, la soumission était juste, et **le fichier restait
     * sur le serveur pour toujours** — le `DELETE /file/public/{uid}` ne partait
     * jamais. C'est ce chemin natif qui met le fichier dans `filesToDelete` puis
     * appelle le provider de stockage, et il n'y avait aucune raison de le
     * refaire.
     *
     * L'URL locale est libérée avant, pendant que l'entrée est encore là : après
     * la délégation, l'index ne désigne plus le même fichier.
     */
    removeFile(index: number): void {
      const self = this as any;
      const retire = (self.dataValue || [])[index];
      if (!retire) return;

      const cle = this.localKey(retire);
      const url = this._localUrls.get(cle);
      if (url) {
        URL.revokeObjectURL(url);
        this._localUrls.delete(cle);
      }

      self.handleFileToRemove(retire);
      self.emit('fileRemoved', { file: retire, component: self });
    }

    /**
     * L'identifiant que le service de fichiers a donné à cette pièce.
     *
     * Rangé sous `data` par le provider de stockage. Les deux formes sont lues :
     * l'objet depuis la correction du provider, et le tableau brut que portent
     * encore les soumissions antérieures.
     */
    fileUid(file: any): string {
      const data = file?.data;
      const doc = Array.isArray(data) ? data[0] : data;
      return String(doc?.uid || doc?.documentId || '');
    }

    /**
     * L'adresse de téléchargement : `/file/{uid}/download/public`.
     *
     * Ce n'est ni l'adresse de dépôt (`/file/public`) ni celle de suppression
     * (`/file/public/{uid}`) : le service expose trois routes distinctes, et
     * c'est celle-ci qui renvoie le fichier en pièce jointe.
     *
     * La racine est déduite de l'adresse rangée dans la soumission plutôt que
     * recomposée depuis `environment` : c'est la seule qui soit forcément celle
     * du service qui a reçu le fichier, quel que soit ce que le schéma du
     * formulaire a désigné comme URL de dépôt.
     */
    downloadUrl(file: any): string {
      const uid = this.fileUid(file);
      const rangee = String(file?.url || '');
      if (!uid || !rangee.includes('/file/')) {
        // Fichier sans identifiant de service — un base64, ou une pièce d'un
        // formulaire legacy : son adresse est ce qu'on a de mieux.
        return this.viewUrl(file);
      }
      return fileDisplayUrl(uid, rangee);
    }

    /**
     * Télécharger la pièce.
     *
     * Un lien ouvert plutôt que `getFile()` de Form.io : le natif demande au
     * provider de stockage de résoudre l'adresse, et il retombe alors sur celle
     * rangée dans la soumission — qui identifie le fichier, mais n'est pas la
     * route de téléchargement.
     *
     * Sans attribut `download` : il est de toute façon ignoré par le navigateur
     * sur une origine tierce.
     *
     * RÉSERVE — la route répond `Content-Disposition: inline` (mesuré, et
     * `isAttachment` n'y change rien). Le lien ouvre donc un onglet qui AFFICHE
     * la pièce, au lieu de l'enregistrer. Obtenir un vrai `attachment` demande
     * le flux en deux temps : `GET /file/public/{uid}/preview` puis
     * `GET /file/dl/<token>/download`. Non fait ici, pour ne pas mêler un appel
     * réseau supplémentaire à la correction d'adresse.
     */
    downloadFile(file: any): void {
      const url = this.downloadUrl(file);
      if (!url) return;

      const lien = document.createElement('a');
      lien.href = url;
      lien.target = '_blank';
      lien.rel = 'noopener';
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
    }

    /**
     * Montrer le fichier dans une fenêtre plutôt que dans un onglet.
     *
     * Un onglet quitte le formulaire ; sur un téléphone, cela veut dire revenir
     * à une page qui a pu être libérée entre-temps, avec ce qui y était saisi.
     */
    previewFile(file: any): void {
      const self = this as any;
      const url = this.viewUrl(file);
      if (!url) return;

      const fond = document.createElement('div');
      fond.className = 'gndad-file-preview-backdrop';

      const cadre = document.createElement('div');
      cadre.className = 'gndad-file-preview';

      if (this.isImage(file)) {
        const image = document.createElement('img');
        image.className = 'gndad-file-preview-image';
        image.alt = this.displayName(file);
        image.src = url;
        cadre.appendChild(image);
      } else {
        // Un document s'affiche dans un cadre : la hauteur y est fixée, parce
        // qu'un `iframe` sans hauteur se réduit à rien.
        const frame = document.createElement('iframe');
        frame.className = 'gndad-file-preview-frame';
        frame.title = this.displayName(file);
        frame.src = url;
        cadre.appendChild(frame);
      }

      const surTouche = (event: KeyboardEvent) => {
        if (event.key === 'Escape') fermer();
      };
      const fermer = () => {
        document.removeEventListener('keydown', surTouche);
        fond.remove();
      };

      const bouton = document.createElement('button');
      bouton.type = 'button';
      bouton.className = 'gndad-file-preview-close';
      bouton.setAttribute('aria-label', self.t(T.CLOSE));
      bouton.textContent = '×';
      bouton.addEventListener('click', fermer);
      cadre.appendChild(bouton);

      // Le fond ferme, le contenu non : un clic sur l'image ne doit pas
      // escamoter ce qu'on est en train de regarder.
      fond.addEventListener('click', (event) => {
        if (event.target === fond) fermer();
      });
      document.addEventListener('keydown', surTouche);

      fond.appendChild(cadre);
      document.body.appendChild(fond);
      bouton.focus();
    }

    // -----------------------------------------------------------------------
    // Lecture d'un fichier — nom, poids, type, URL
    // -----------------------------------------------------------------------

    /** Le nom montré à l'usager, celui qu'il a déposé. */
    displayName(file: any): string {
      return String(file?.originalName || file?.name || '');
    }

    /** Le poids en octets, quel que soit le storage. */
    sizeBytes(file: any): number {
      return Number(file?.size) || 0;
    }

    /** Clé de suivi d'une URL locale, stable tant que le fichier est en place. */
    private localKey(file: any): string {
      return `${this.displayName(file)}:${this.sizeBytes(file)}`;
    }

    /** Vrai si le fichier peut se montrer comme une image. */
    isImage(file: any): boolean {
      const type = String(file?.type || '');
      if (type.startsWith('image/')) return true;
      const nom = this.displayName(file).toLowerCase();
      const point = nom.lastIndexOf('.');
      return point > -1 && IMAGE_EXTENSIONS.includes(nom.slice(point + 1));
    }

    /**
     * L'URL utilisable pour afficher le fichier.
     *
     * Le blob local d'abord, l'adresse du storage ensuite. Dans cet ordre parce
     * que le local est disponible à l'instant où le fichier est choisi et que
     * l'autre ne l'est pas — et parce que c'est la même image, donc préférer
     * celle qui ne demande rien au réseau est gratuit.
     *
     * `file.url` couvre les deux storages du portail : en base64 il porte le
     * data URI complet, en `url` il porte l'adresse servie par le back — déjà
     * préfixée le cas échéant par `prefixLegacyFileUrls`.
     *
     * Quand la pièce porte un `uid` de service, l'adresse est RECALCULÉE plutôt
     * que lue. C'est ce qui répare les brouillons déjà enregistrés : ils
     * portent `/file/public/{uid}`, qui rend du JSON de métadonnées et non le
     * fichier — un `<img src>` dessus ne montre rien. Le défaut restait
     * invisible tant que l'onglet était ouvert, puisque le `blob:` local passe
     * en premier ; il n'apparaissait qu'en rouvrant le dossier.
     *
     * Sans `uid`, rien n'est recalculé : un base64 garde son data URI, et une
     * pièce de formulaire legacy garde l'adresse de SON service — ce chemin-là
     * ne connaît pas les routes du portail et ne doit pas s'y voir ramené.
     */
    viewUrl(file: any): string {
      const local = this._localUrls.get(this.localKey(file));
      if (local) return local;
      const rangee = String(file?.url || file?.data?.url || '');
      return fileDisplayUrl(this.fileUid(file), rangee);
    }

    /** L'URL de la vignette, ou vide si le fichier n'en a pas. */
    thumbnailUrl(file: any): string {
      if (!file || !this.isImage(file)) return '';
      return this.viewUrl(file);
    }

    /** La forme de la vignette ; sa taille vient du schéma. */
    get thumbnailStyle(): string {
      const self = this as any;
      const largeur = self.component.thumbnailWidth || '4rem';
      const hauteur = self.component.thumbnailHeight || '4rem';
      return `width:${largeur};height:${hauteur}`;
    }

    /**
     * Un nom long, raccourci au milieu.
     *
     * Au milieu et non à la fin : l'extension dit ce qu'est le fichier, et un
     * nom coupé par la fin dit tout sauf cela.
     */
    shortName(name: string): string {
      const texte = String(name || '');
      if (texte.length <= NAME_MAX_LENGTH) return texte;
      return `${texte.slice(0, 20)}…${texte.slice(-11)}`;
    }

    /**
     * Un poids en octets, écrit court.
     *
     * Le nombre passe par `Intl` — la virgule décimale et les séparateurs de
     * milliers ne valent pas d'être réécrits — et l'unité par le dictionnaire :
     * l'abréviation d'un octet est une affaire de langue, et `Intl` écrit le mot
     * « byte » en toutes lettres pour les petits fichiers.
     */
    fileSize(size: number): string {
      const self = this as any;
      const octets = Number(size) || 0;
      if (!octets) return '';
      const index = Math.min(3, Math.floor(Math.log(octets) / Math.log(1024)));
      const valeur = octets / (1024 ** index);
      const decimales = index ? 1 : 0;

      let nombre: string;
      try {
        nombre = new Intl.NumberFormat(self.options?.language || 'fr', {
          minimumFractionDigits: decimales,
          maximumFractionDigits: decimales,
        }).format(valeur);
      } catch {
        // Une étiquette de langue qu'`Intl` refuse d'analyser.
        nombre = valeur.toFixed(decimales);
      }
      const unite = self.t([T.UNIT_B, T.UNIT_KB, T.UNIT_MB, T.UNIT_GB][index]);
      return `${nombre} ${unite}`;
    }

    // -----------------------------------------------------------------------
    // Les contraintes annoncées avant le dépôt
    // -----------------------------------------------------------------------

    /**
     * Les formats acceptés, écrits pour un lecteur plutôt que pour un
     * navigateur. `filePattern` s'écrit `.pdf,.jpg` ou `image/*` : la première
     * forme se lit très bien une fois le point retiré et la casse relevée, la
     * seconde ne se lit pas du tout et part donc telle quelle.
     */
    acceptedFormats(): string {
      const motif = String((this as any).component.filePattern || '').trim();
      if (!motif || motif === '*') return '';
      return motif
        .split(',')
        .map((entree) => entree.trim())
        .filter(Boolean)
        .map((entree) => (entree.startsWith('.') ? entree.slice(1).toUpperCase() : entree))
        .join(', ');
    }

    /** Un poids déclaré — « 5MB », « 200KB », un nombre — en octets. */
    parseSize(size: string | number): number {
      const trouve = /^([\d.]+)\s*([kmgt]?b)?$/i.exec(String(size).trim());
      if (!trouve) return 0;
      const quantite = Number(trouve[1]);
      if (!Number.isFinite(quantite)) return 0;
      const echelle: Record<string, number> = {
        b: 1, kb: 1024, mb: 1024 ** 2, gb: 1024 ** 3, tb: 1024 ** 4,
      };
      return quantite * (echelle[(trouve[2] || 'b').toLowerCase()] ?? 1);
    }

    /** La phrase d'annonce, ou rien s'il n'y a rien à annoncer. */
    constraintsText(): string {
      const self = this as any;
      if (self.component.announceConstraints === false) return '';

      const morceaux: string[] = [];
      const formats = this.acceptedFormats();
      if (formats) {
        morceaux.push(`${self.t(T.ACCEPTED_FORMATS)} : ${formats}`);
      }

      // Le poids n'est annoncé que si quelqu'un l'a choisi. Form.io en pose un
      // par défaut — un gigaoctet — qui ne contraint rien de réel, et l'annoncer
      // sous chaque champ de dépôt ajouterait une ligne de bruit sous chacun.
      const declare = self.component.fileMaxSize;
      const natif = (Formio as any).Components?.components?.file?.schema?.()?.fileMaxSize;
      if (declare && declare !== natif) {
        const octets = this.parseSize(declare);
        morceaux.push(`${self.t(T.MAX_SIZE)} : ${octets ? this.fileSize(octets) : declare}`);
      }
      return morceaux.join(' · ');
    }

    /** Les URLs locales sont libérées avec le composant. */
    destroy(...args: any[]): any {
      this._localUrls.forEach((url) => URL.revokeObjectURL(url));
      this._localUrls.clear();
      return super.destroy(...args);
    }
  };
}

/** Vrai une fois l'enregistrement fait, pour qu'un second appel soit inerte. */
let enregistre = false;

/**
 * Enregistre le champ fichier GNDAD sous la clé `file`.
 *
 * Sous la même clé que le natif, et c'est le point : aucune définition de
 * formulaire n'a à changer, aucun schéma servi par le catalogue n'a à être
 * republié. Le type reste `file`, et ce que la soumission porte reste identique.
 *
 * Idempotent : appelé deux fois, le second appel ne fait rien — sans quoi la
 * classe hériterait d'elle-même et chaque appel empilerait un niveau de plus.
 */
export function registerGndadFileComponent(): void {
  if (enregistre) return;
  const Components = (Formio as any)?.Components;
  const NativeFile = Components?.components?.file;
  if (!Components || !NativeFile) return;

  Components.setComponent('file', createGndadFileComponent(NativeFile));
  enregistre = true;
}
